import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import OrdersList from 'components/Orders/OrderList/OrdersList';
import ConfirmationModal from 'components/UI/ConfimationModal';
import Modal from 'components/UI/Modal';
import UploadComponent from 'components/Product/UploadFileComponent';
import { useAPI } from 'lib/API';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { loadCommissionData, updateTransactionStatus } from 'requests/affiliateMember';
import { AffiliateCommissionData, CommissionActionMap } from 'data/AffiliateMember';
import { AFFILIATE_STRINGS, COMMISSION_TRANSACTION_TYPES } from 'constants/affiliate';
import { GENERAL_CONSTANTS, STORAGE_KEYS } from 'constants/general';
import { getItems } from 'lib/SessionStorage';
import { Main } from 'styles/themeColors';
import { BusinessUnit } from 'data/BusinessUnit';
import { useQueryParams } from 'hooks/useHookRouterCompat';
import { validateFile } from 'utils/file';

const PDF_VALIDATION_OPTIONS = { acceptedMimeTypes: ['application/pdf', '.pdf'], maxSizeMB: 20 };

export interface CommissionActionButtons {
  canApprove: boolean;
  canUnapprove: boolean;
  canPay: boolean;
  commissionSum: number;
  isLoading: boolean;
  onApprove: () => void;
  onUnapprove: () => void;
  onPay: () => void;
}

interface Props {
  customerId: number;
  brandCode: string;
  selectedVendor?: any;
  selectedBusinessUnit?: BusinessUnit;
  featureFlags: any;
  initialAffiliateData: AffiliateCommissionData[] | null;
  onActionButtonsChange?: (buttons: CommissionActionButtons | null) => void;
  consignmentIds?: string;
  selectedAffiliateId?: number | null;
}

const CommissionOrders: React.FC<Props> = ({
  customerId,
  brandCode,
  selectedVendor,
  selectedBusinessUnit,
  featureFlags,
  initialAffiliateData,
  onActionButtonsChange,
  consignmentIds,
  selectedAffiliateId
}) => {
  const [queryParams, setQueryParams] = useQueryParams();

  const commissionStatusFromUrl = typeof queryParams.commissionStatus === 'string' ? queryParams.commissionStatus : '';

  const commissionActions = useMemo<CommissionActionMap | null>(
    () => getItems(STORAGE_KEYS.COMMISSION_ACTIONS),
    []
  );

  const defaultCommissionStatus = commissionActions?.approve.fromStatus ?? '';

  const [commissionStatus, setCommissionStatus] = useState<string>(commissionStatusFromUrl || defaultCommissionStatus);

  useEffect(() => {
    const nextStatus = commissionStatusFromUrl || defaultCommissionStatus;
    setCommissionStatus(currentStatus => (nextStatus !== currentStatus ? nextStatus : currentStatus));
  }, [commissionStatusFromUrl, defaultCommissionStatus]);

  const urlInitializedRef = useRef(false);
  useEffect(() => {
    if (urlInitializedRef.current) return;
    urlInitializedRef.current = true;
    setQueryParams({ commissionStatus });
  });

  const handleCommissionStatusChange = useCallback((next: string) => {
    if (!next) return;
    setQueryParams({ commissionStatus: next });
  }, [setQueryParams]);

  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
  const [selectedConsignments, setSelectedConsignments] = useState<string[]>([]);
  const [affiliateData, setAffiliateData] = useState<AffiliateCommissionData[] | null>(initialAffiliateData);

  useEffect(() => {
    setAffiliateData(initialAffiliateData);
  }, [initialAffiliateData]);

  const ordersListRef = useRef<{ refresh: () => void }>(null);

  const [confirmAction, setConfirmAction] = useState<{
    action: string;
    label: string;
  } | null>(null);

  const selectable = useMemo(
    () => commissionActions
      ? new Set(Object.values(commissionActions).map(a => a.fromStatus))
      : new Set<string>(),
    [commissionActions]
  );

  const [paymentFile, setPaymentFile] = useState<{ imageFile: [{ name: string; size: number }] }>({ imageFile: [] as any });
  const [paymentFileObject, setPaymentFileObject] = useState<File | null>(null);
  const [uploadFileSizeError, setUploadFileSizeError] = useState<boolean>(false);
  const [uploadFileTypeError, setUploadFileTypeError] = useState<boolean>(false);
  const paymentInputRef = useRef<HTMLInputElement | null>(null);

  const commissionDataReq = useAPI({
    deferFn: loadCommissionData,
    onResolve: (result: any) => {
      const affiliates = result.affiliates || [];
      setAffiliateData(affiliates.length > 0 ? affiliates : null);
    },
    onReject: (err: Error) => renderToast(toast.TYPE.ERROR, err.message)
  });
  
  const commissionDataRunRef = useRef(commissionDataReq.run);
  commissionDataRunRef.current = commissionDataReq.run;

  const isCommissionStatusMounted = useRef(false);
  useEffect(() => {
    if (!isCommissionStatusMounted.current) {
      isCommissionStatusMounted.current = true;
      return;
    }
    setSelectedOrders([]);
    setSelectedConsignments([]);
    if (customerId && selectedAffiliateId) {
      commissionDataRunRef.current(customerId, selectedAffiliateId);
    }
  }, [commissionStatus, customerId, selectedAffiliateId]);

  const updateStatusReq = useAPI({
    deferFn: updateTransactionStatus,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'Transactions updated successfully');
      if (selectedAffiliateId) commissionDataReq.run(customerId, selectedAffiliateId);
      setConfirmAction(null);
      setPaymentFile({ imageFile: [] as any });
      setPaymentFileObject(null);
      setUploadFileSizeError(false);
      setUploadFileTypeError(false);
      setSelectedOrders([]);
      setSelectedConsignments([]);
      ordersListRef.current?.refresh();
    },
    onReject: (err: Error) => renderToast(toast.TYPE.ERROR, err.message)
  });

  const affiliateCode = affiliateData?.[0]?.affiliateCode ?? null;

  const allTransactions = useMemo(() =>
    (affiliateData || []).flatMap((a: AffiliateCommissionData) => a.transactions as any[])
      .filter((t: any) => t.type !== COMMISSION_TRANSACTION_TYPES.PAYOUT),
    [affiliateData]
  );

  // Pre-select consignments from consignmentIds prop on mount
  useEffect(() => {
    if (consignmentIds) {
      const ids = consignmentIds.split(',').filter(Boolean);
      if (ids.length > 0) {
        setSelectedConsignments(ids);
      }
    }
  }, [consignmentIds]);

  // Build a map of consignmentCode -> commission status from transaction data (1:1, no overwrites)
  const consignmentStatusMap = useMemo(() => {
    const map: Record<string, string> = {};
    allTransactions.forEach((t: any) => {
      map[t.consignmentCode] = t.status;
    });
    return map;
  }, [allTransactions]);

  const consignmentCommissionMap = useMemo(() => {
    const map: Record<string, number> = {};
    allTransactions.forEach((t: any) => {
      map[t.consignmentCode] = map[t.consignmentCode] ? map[t.consignmentCode] + t.commissionAmount : t.commissionAmount || 0;
    });
    return map;
  }, [allTransactions]);

  // Consignment IDs derived purely from selected order rows (not manual checkbox toggles)
  const orderDerivedConsignmentIds = useMemo(() => {
    const orderDerivedSet = new Set(selectedOrders.flatMap((o: any) =>
      (o.consignments || [])
        .filter((c: any) => {
          const status = consignmentStatusMap[c.consignmentId];
          return selectable.has(status) && (!commissionStatus || status === commissionStatus);
        })
        .map((c: any) => c.consignmentId)
    ));
    return orderDerivedSet;
  }, [selectedOrders, consignmentStatusMap, selectable, commissionStatus]);

  const selectedConsignmentIds = useMemo(() =>
    Array.from(new Set(Array.from(orderDerivedConsignmentIds).concat(selectedConsignments))),
  [orderDerivedConsignmentIds, selectedConsignments]);

  // When individual checkboxes are toggled, store only the manually-selected IDs
  // (exclude order-derived ones) so raw state never accumulates stale IDs.
  const handleConsignmentToggle = useCallback((nextIds: string[]) => {
    setSelectedConsignments(nextIds.filter((id: string) => !orderDerivedConsignmentIds.has(id)));
  }, [orderDerivedConsignmentIds]);

  const handleSelectionChange = useCallback((rows: any[]) => {
    setSelectedOrders(rows);
  }, []);

  const handlePaymentFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (paymentInputRef.current) paymentInputRef.current.value = '';
      if (!f) return;

      const { typeError, sizeError, valid } = validateFile(f, PDF_VALIDATION_OPTIONS);

      setUploadFileTypeError(typeError);
      setUploadFileSizeError(sizeError);
      setPaymentFile({ imageFile: [{ name: f.name, size: f.size }] as any });
      setPaymentFileObject(valid ? f : null);
    },
    []
  );

  const handlePaymentClear = useCallback(() => {
    setPaymentFile({ imageFile: [] as any });
    setPaymentFileObject(null);
    setUploadFileSizeError(false);
    setUploadFileTypeError(false);
    if (paymentInputRef.current) paymentInputRef.current.value = '';
  }, []);

  const handleAction = useCallback((action: string, label: string) => {
    setConfirmAction({ action, label });
  }, []);

  const actionFromStatusMap = useMemo<Record<string, string>>(
    () => commissionActions
      ? Object.values(commissionActions).reduce<Record<string, string>>((acc, a) => {
          acc[a.toStatus] = a.fromStatus;
          return acc;
        }, {})
      : {},
    [commissionActions]
  );

  const handleConfirmAction = useCallback(() => {
    if (!confirmAction || !affiliateData || selectedConsignmentIds.length === 0) return;

    // Find matching transactions by consignment code
    const matchingTransactions = allTransactions.filter(
      (t: any) => selectedConsignmentIds.includes(t.consignmentCode)
    );

    // Only include transactions whose current status matches the required source status for this action
    const fromStatus = actionFromStatusMap[confirmAction.action];
    const filteredTransactions = matchingTransactions.filter(
      (t: any) => t.status === fromStatus
    );

    // Group by customerAffiliateId to call API per affiliate
    const byAffiliate: Record<number, number[]> = {};
    filteredTransactions.forEach((t: any) => {
      if (!byAffiliate[t.customerAffiliateId]) byAffiliate[t.customerAffiliateId] = [];
      byAffiliate[t.customerAffiliateId].push(t.id);
    });

    if (Object.keys(byAffiliate).length === 0) {
      renderToast(toast.TYPE.ERROR, 'No matching commission transactions found');
      return;
    }

    // Call updateStatusReq.run for each affiliate group
    Object.entries(byAffiliate).forEach(([caId, txIds]) => {
      updateStatusReq.run(Number(caId), txIds, selectedBusinessUnit?.brand?.id, confirmAction.action, paymentFileObject ?? undefined);
    });
  }, [confirmAction, affiliateData, selectedConsignmentIds, allTransactions, updateStatusReq, actionFromStatusMap]);

  const canApprove = useMemo(() =>
    !!commissionActions &&
    selectedConsignmentIds.length > 0 && selectedConsignmentIds.some(id =>
      allTransactions.some(t => t.consignmentCode === id && t.status === commissionActions.approve.fromStatus)
    ),
    [selectedConsignmentIds, allTransactions, commissionActions]
  );

  const canUnapprove = useMemo(() =>
    !!commissionActions &&
    selectedConsignmentIds.length > 0 && selectedConsignmentIds.some(id =>
      allTransactions.some(t => t.consignmentCode === id && t.status === commissionActions.unapprove.fromStatus)
    ),
    [selectedConsignmentIds, allTransactions, commissionActions]
  );

  const canPay = useMemo(() =>
    !!commissionActions &&
    selectedConsignmentIds.length > 0 && selectedConsignmentIds.some(id =>
      allTransactions.some(t => t.consignmentCode === id && t.status === commissionActions.pay.fromStatus)
    ),
    [selectedConsignmentIds, allTransactions, commissionActions]
  );

  // Sum commission amounts for selected consignments from transaction data
  const selectedCommissionSum = useMemo(() => {
    if (allTransactions.length === 0 || selectedConsignmentIds.length === 0) return 0;
    return allTransactions
      .filter((t: any) => selectedConsignmentIds.includes(t.consignmentCode))
      .reduce((sum: number, t: any) => sum + (t.commissionAmount || 0), 0);
  }, [selectedConsignmentIds, allTransactions]);

  const isLoading = updateStatusReq.isLoading;
  const isPayModal = confirmAction?.action === commissionActions?.pay.toStatus;
  const isConfirmDisabled = isLoading || (isPayModal && paymentFileObject === null);

  // Track whether the orders grid has finished its initial load
  const [ordersGridLoaded, setOrdersGridLoaded] = useState(false);

  // Notify parent about action button state (only after orders grid has loaded)
  useEffect(() => {
    if (onActionButtonsChange && ordersGridLoaded) {
      const hasSelected = selectedConsignmentIds.length > 0;
      onActionButtonsChange(hasSelected ? {
        canApprove,
        canUnapprove,
        canPay,
        commissionSum: selectedCommissionSum,
        isLoading,
        onApprove: () => handleAction(commissionActions!.approve.toStatus, AFFILIATE_STRINGS.buttons.approveSelected),
        onUnapprove: () => handleAction(commissionActions!.unapprove.toStatus, AFFILIATE_STRINGS.buttons.unapproveSelected),
        onPay: () => handleAction(commissionActions!.pay.toStatus, AFFILIATE_STRINGS.buttons.paySelected)
      } : null);
    }
  }, [selectedConsignmentIds, canApprove, canUnapprove, canPay, selectedCommissionSum, isLoading, onActionButtonsChange, handleAction, commissionActions, ordersGridLoaded]);

  if (commissionDataReq.isLoading) {
    return <LoadingIndicator center />;
  }

  return (
    <div>
      <OrdersList
        ref={ordersListRef}
        selectedVendor={selectedVendor}
        selectedBusinessUnit={selectedBusinessUnit}
        featureFlags={featureFlags}
        affiliateCustomerId={customerId}
        shouldRedirect={false}
        enableSelection
        selectedRows={selectedOrders}
        onSelectionChange={handleSelectionChange}
        consignmentStatusMap={consignmentStatusMap}
        consignmentCommissionMap={consignmentCommissionMap}
        selectedConsignments={selectedConsignmentIds}
        onConsignmentSelect={handleConsignmentToggle}
        isRowSelectable={(row: any) =>
          (row.consignments || []).some((c: any) => {
            return selectable.has(consignmentStatusMap[c.consignmentId]);
          })
        }
        commissionStatus={commissionStatus}
        onCommissionStatusChange={handleCommissionStatusChange}
        paidCommissionStatus={commissionActions?.pay.toStatus}
        consignmentIds={consignmentIds}
        affiliateCode={affiliateCode}
        onDataLoaded={() => setOrdersGridLoaded(true)}
        pagePadding='24px 0px 0px 0px'
      />

      {confirmAction && confirmAction.action === commissionActions?.pay.toStatus && (
        <Modal
          showModal={!!confirmAction}
          onClose={() => {
            if (isLoading) return;
            setConfirmAction(null);
            setPaymentFile({ imageFile: [] as any });
            setPaymentFileObject(null);
            setUploadFileSizeError(false);
            setUploadFileTypeError(false);
          }}
          onClick={handleConfirmAction}
          title={confirmAction.label}
          primaryButtonText={GENERAL_CONSTANTS.CONFIRM}
          ghostButtonText={GENERAL_CONSTANTS.CANCEL}
          showCloseIcon
          color={Main.black}
          primaryButtonColor={Main.brandDark}
          borderColor={Main.disabled}
          disabled={isConfirmDisabled}
          ghostButtonDisabled={isLoading}
        >
          <p>You are about to mark the selected commission orders as paid. Please attach the payment receipt before proceeding.</p>
          <div style={{ marginTop: '16px' }}>
            <UploadComponent
              uploadSuccess={false}
              fileUploading={isLoading}
              file={paymentFile}
              handleUploadClick={() => paymentInputRef.current?.click()}
              handleClear={handlePaymentClear}
              inputRef={paymentInputRef}
              handleFileChange={handlePaymentFileChange}
              uploadFileSizeError={uploadFileSizeError}
              uploadFileTypeError={uploadFileTypeError}
              uploadFileTemplateError={false}
              acceptedFileTypes="application/pdf,.pdf"
              fileTypeErrorMessage="You can only upload PDF file format"
              buttonText="Upload Payment Receipt"
            />
          </div>
        </Modal>
      )}

      {confirmAction && confirmAction.action === commissionActions?.approve.toStatus && (
        <ConfirmationModal
          show={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirmAction}
          title={confirmAction.label}
          message="You are about to approve the selected commission orders for payment. Do you want to proceed?"
          disabled={isLoading}
        />
      )}

      {confirmAction && confirmAction.action === commissionActions?.unapprove.toStatus && (
        <ConfirmationModal
          show={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirmAction}
          title={confirmAction.label}
          message="You are about to revert approval for the selected commission orders. Do you want to proceed?"
          disabled={isLoading}
        />
      )}
    </div>
  );
};

export default CommissionOrders;
